import React, { useEffect, useState, useCallback } from "react";
import { Table, Button, Modal, Form, Input, Checkbox, Select, Collapse } from "antd";
import { FilterOutlined } from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import { Device } from "./types";
import { useDevices } from "./DeviceProvider";

interface DeviceListModuleProps {
  onDeviceSelected?: (device: Device) => void;
  onDevicesSelected?: (devices: Device[]) => void;
}

const { Option } = Select;
const { Panel } = Collapse;

// localStorageに保存するためのキー
const LOCAL_STORAGE_KEY = "deviceListFilterSettings";

// 保存する設定の型定義
interface FilterSettings {
  filters: any;
  pageSize: number;
}

// ネストされたオブジェクトから値を取得するヘルパー関数
const getNestedValue = (obj: any, path: string) => {
  return path.split('.').reduce((acc, part) => acc && acc[part], obj);
};

const DeviceListModule: React.FC<DeviceListModuleProps> = ({ onDeviceSelected, onDevicesSelected }) => {
  const { devices, isLoading, isError } = useDevices();
  const [filteredDeviceData, setFilteredDeviceData] = useState<Device[]>([]);
  const [selectedDevices, setSelectedDevices] = useState<React.Key[]>([]);
  const [isFilterModalVisible, setIsFilterModalVisible] = useState(false);
  const [filterForm] = Form.useForm();
  const [pageSize, setPageSize] = useState<number>(10);
  const [selectedRowId, setSelectedRowId] = useState<string | null>(null);
  const [isFiltered, setIsFiltered] = useState(false);

  // フィルタリングロジックをカプセル化
  const applyFilter = useCallback((allDevices: Device[], filters: any): Device[] => {
    if (!filters || Object.keys(filters).length === 0) {
      return allDevices;
    }
    return allDevices.filter((device) =>
      Object.keys(filters).every((key) => {
        const value = filters[key];
        if (key === "checkedDevices") return !value || selectedDevices.includes(device.id);
        if (value === null || value === undefined || value === '') return true;
        
        // "userInfo.userName" のようなドット記法のキーに対応
        const fieldValue = getNestedValue(device, key);

        return fieldValue?.toString().toLowerCase().includes(value.toString().toLowerCase());
      })
    );
  }, [selectedDevices]);

  // コンポーネントのマウント時およびデバイスデータ更新時に設定を読み込み、フィルタを適用
  useEffect(() => {
    if (devices) {
      try {
        const savedSettings = localStorage.getItem(LOCAL_STORAGE_KEY);
        if (savedSettings) {
          const { filters, pageSize: savedPageSize } = JSON.parse(savedSettings) as FilterSettings;
          
          filterForm.setFieldsValue(filters || {});
          setPageSize(savedPageSize || 10);

          const filtered = applyFilter(devices, filters || {});
          setFilteredDeviceData(filtered);
          
          const hasFilters = filters && Object.values(filters).some(v => v);
          setIsFiltered(!!hasFilters);

        } else {
          setFilteredDeviceData(devices);
          setIsFiltered(false);
        }
      } catch (error) {
        console.error("Failed to parse filter settings from localStorage", error);
        setFilteredDeviceData(devices); // エラー時は全件表示
      }
    }
  }, [devices, applyFilter, filterForm]);


  if (isLoading) return <div>読み込み中...</div>;
  if (isError) return <div>データ取得エラー</div>;

  // 設定をlocalStorageに保存する関数
  const saveSettings = (filters: any, size: number) => {
    try {
      const settings: FilterSettings = { filters, pageSize: size };
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(settings));
    } catch (error) {
      console.error("Failed to save filter settings to localStorage", error);
    }
  };

  const handleFilter = (values: any) => {
    if (!devices) return;
    
    const filtered = applyFilter(devices, values);
    setFilteredDeviceData(filtered);
    setIsFilterModalVisible(false);

    const hasFilters = Object.values(values).some(v => v);
    setIsFiltered(hasFilters);
    
    saveSettings(values, pageSize);
  };

  const handleClearFilter = () => {
    filterForm.resetFields();
    setFilteredDeviceData(devices || []);
    setIsFiltered(false);
    setPageSize(10);
    localStorage.removeItem(LOCAL_STORAGE_KEY);
  };
  
  const handlePageSizeChange = (value: number) => {
    setPageSize(value);
    saveSettings(filterForm.getFieldsValue(), value);
  };

  const columns: ColumnsType<Device> = [
    { title: "シリアル番号", dataIndex: "serialNumber", key: "serialNumber", sorter: (a, b) => a.serialNumber.localeCompare(b.serialNumber) },
    { title: "デバイス名", dataIndex: "deviceName", key: "deviceName" },
    { title: "ユーザー名", dataIndex: ["userInfo", "userName"], key: "userInfo.userName" },
    { title: "担当者名", dataIndex: "personName", key: "personName" },
    { title: "会社ID", dataIndex: "companyID", key: "companyID" },
    { title: "組織ID", dataIndex: "orgID", key: "orgID" },
    { title: "電話番号", dataIndex: "telNo", key: "telNo" },
    { title: "最終同期", dataIndex: "lastSyncTime", key: "lastSyncTime", render: (v) => v ? new Date(typeof v === 'number' && v < 1e12 ? v * 1000 : v).toLocaleString('ja-JP') : "N/A" },
  ];

  const filterButtonStyle = isFiltered
  ? {
      backgroundColor: "#faad14", // オレンジ
      borderColor: "#faad14",
      color: "white",
    }
  : {};

  return (
    <div style={{ marginBottom: 0 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
        <div>{/* 左側のスペース確保用 */}</div>
        <div style={{ display: "flex", alignItems: "center" }}>
          <Button 
            type="primary"
            onClick={() => setIsFilterModalVisible(true)} 
            style={{ marginRight: 8, ...filterButtonStyle }}
            icon={<FilterOutlined />}
          >
            フィルタ
          </Button>
          <Button onClick={handleClearFilter} style={{ marginRight: 16 }}>フィルタ解除</Button>
          <div>
            <span style={{ marginRight: 8 }}>最大件数:</span>
            <Select value={pageSize} onChange={handlePageSizeChange} style={{ width: 120 }}>
              <Option value={10}>10件</Option>
              <Option value={50}>50件</Option>
              <Option value={100}>100件</Option>
            </Select>
          </div>
        </div>
      </div>

      <Table
        className="device-table"
        rowSelection={{
          type: "checkbox",
          selectedRowKeys: selectedDevices,
          onChange: (selectedRowKeys, selectedRows) => {
            setSelectedDevices(selectedRowKeys);
            onDevicesSelected?.(selectedRows);
          },
        }}
        rowClassName={(record) => `row-hover ${selectedRowId === record.id ? "selected-row" : ""}`}
        onRow={(record) => ({
          onClick: () => {
            onDeviceSelected?.(record);
            setSelectedRowId(record.id); 
          },
        })}
        columns={columns}
        dataSource={filteredDeviceData}
        rowKey="id"
        pagination={{ pageSize }}
        scroll={{ x: "max-content" }}
        style={{ width: "100%" }}
      />
      <Modal title="デバイスのフィルタ" open={isFilterModalVisible} onCancel={() => setIsFilterModalVisible(false)} onOk={() => filterForm.submit()}>
        <Form form={filterForm} layout="vertical" onFinish={handleFilter}>
          <Form.Item name="serialNumber" label="シリアル番号"><Input allowClear /></Form.Item>
          <Form.Item name="deviceName" label="デバイス名"><Input allowClear /></Form.Item>
          <Form.Item name="userInfo.userName" label="ユーザー名"><Input allowClear /></Form.Item>
          <Form.Item name="personName" label="担当者名"><Input allowClear /></Form.Item>
          <Form.Item name="checkedDevices" valuePropName="checked">
            <Checkbox>チェックされたデバイスのみ</Checkbox>
          </Form.Item>
          <Collapse ghost>
            <Panel header="その他詳細項目" key="1">
              <Form.Item name="companyID" label="会社ID"><Input allowClear /></Form.Item>
              <Form.Item name="orgID" label="組織ID"><Input allowClear /></Form.Item>
              <Form.Item name="personID" label="担当者ID"><Input allowClear /></Form.Item>
              <Form.Item name="telNo" label="電話番号"><Input allowClear /></Form.Item>
              <Form.Item name="osVersion" label="OSバージョン"><Input allowClear /></Form.Item>
              <Form.Item name="appVersion" label="アプリバージョン"><Input allowClear /></Form.Item>
              <Form.Item name="networkStatus" label="ネットワーク状態"><Input allowClear /></Form.Item>
              <Form.Item name="emergencyTel" label="緊急連絡先電話番号"><Input allowClear /></Form.Item>
              <Form.Item name="emergencyEmail" label="緊急連絡先メール"><Input allowClear /></Form.Item>
              <Form.Item name="userInfo.userID" label="ユーザーID"><Input allowClear /></Form.Item>
              <Form.Item name="lineInfo.lineUserID" label="LINEユーザーID"><Input allowClear /></Form.Item>
            </Panel>
          </Collapse>
        </Form>
      </Modal>
    </div>
  );
};

export default DeviceListModule;

